import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import ChatView from './ChatView';
import actions from '../../store/actions';
import userDummyIcon from '../NewHeader/images/user.svg';
import botAvatar from '../NewHeader/images/sharebot-robot.png';
import './styles.scss';

const shareBotResources = {
    organization: 'ORGANIZATION',
    worldKnowledge: 'WORLD_KNOWLEDGE'
};

function ShareBot({ onHoverStart, onHoverEnd }) {
    const ref = useRef(null);
    const chatContainerRef = useRef(null);
    const textareaRef = useRef(null);

    useEffect(() => {
        const el = ref.current;
        const chatContainer = chatContainerRef.current;

        if (!el) return;

        const stopScroll = (e) => {
            // Always prevent parent container from scrolling
            if (!chatContainerRef.current || !chatContainerRef.current.contains(e.target)) {
                e.preventDefault();
                e.stopPropagation();
                return;
            }

            // For the chat container specifically, check if we're at boundaries
            if (chatContainerRef.current.contains(e.target)) {
                const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
                const isAtTop = scrollTop <= 0;
                const isAtBottom = Math.abs(scrollHeight - scrollTop - clientHeight) <= 1;

                // If at top and trying to scroll up, or at bottom and trying to scroll down
                if ((isAtTop && e.deltaY < 0) || (isAtBottom && e.deltaY > 0)) {
                    e.preventDefault();
                    e.stopPropagation();
                }
            }
        };

        el.addEventListener('wheel', stopScroll, { passive: false });
        el.addEventListener('touchmove', stopScroll, { passive: false });

        return () => {
            el.removeEventListener('wheel', stopScroll);
            el.removeEventListener('touchmove', stopScroll);
        };
    }, []);

    const [showBot, setShowBot] = useState(false);
    const [activeBotResource, setActiveBotResource] = useState(shareBotResources.organization);
    const [orgHistory, setOrgHistory] = useState([]);
    const [worldHistory, setWorldHistory] = useState([]);
    const [message, setMessage] = useState('');

    const conversationHistory = activeBotResource === shareBotResources.organization ? orgHistory : worldHistory;
    const setConversationHistory = activeBotResource === shareBotResources.organization ? setOrgHistory : setWorldHistory;

    const { t } = useTranslation();
    const dispatch = useDispatch();
    const user = useSelector(state => state.user);
    const sharebot = useSelector(state => state.sharebot);

    // Get user avatar
    const userAvatar = getUserAvatar(user);

    useEffect(() => {
        setShowBot(sharebot.showShareBot);
    }, [sharebot.showShareBot]);

    useEffect(() => {
        return () => {
            setOrgHistory([]);
            setWorldHistory([]);
        };
    }, []);

    useEffect(() => {
        if (!showBot) {
            setOrgHistory([]);
            setWorldHistory([]);
            dispatch(actions.sharebot.disableShareBot());
        }
    }, [showBot, dispatch]);

    useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    }, [conversationHistory]);

    // Add an effect to handle chat container scroll behavior
    useEffect(() => {
        if (!chatContainerRef.current) return;

        const chatContainer = chatContainerRef.current;

        const handleChatScroll = (e) => {
            const { scrollTop, scrollHeight, clientHeight } = chatContainer;
            const isAtTop = scrollTop <= 0;
            const isAtBottom = Math.abs(scrollHeight - scrollTop - clientHeight) <= 1;

            // If at top and trying to scroll up, or at bottom and trying to scroll down
            if ((isAtTop && e.deltaY < 0) || (isAtBottom && e.deltaY > 0)) {
                e.preventDefault();
                e.stopPropagation();
            }
        };

        chatContainer.addEventListener('wheel', handleChatScroll, { passive: false });

        return () => {
            chatContainer.removeEventListener('wheel', handleChatScroll);
        };
    }, []);

    const toggleBot = () => setShowBot(val => !val);

    const handleSendMessage = (e) => {
        if (e.key === 'Enter' && !e.shiftKey && message.trim()) {
            e.preventDefault();
            const newUserMessage = {
                type: 'user',
                text: message,
                timestamp: new Date(),
                avatar: userAvatar
            };
            setConversationHistory(prev => [...prev, newUserMessage]);
            setMessage('');
            // Reset textarea height after clearing
            if (textareaRef.current) {
                textareaRef.current.style.height = 'auto';
            }
        }
    };

    const handleMessageChange = (e) => {
        setMessage(e.target.value);
        // Auto-resize textarea
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
        }
    };

    // Helper function to get user avatar
    function getUserAvatar(user) {
        if (user && user.info && user.info.avatar) {
            if (user.info.avatar.resizes && user.info.avatar.resizes.thumbnail) {
                return user.info.avatar.resizes.thumbnail;
            } else if (user.info.avatar.link) {
                return user.info.avatar.link;
            }
        }
        return userDummyIcon;
    }

    return (
        <div className="sharebot-wrapper" onMouseEnter={onHoverStart}
            ref={ref}
            onMouseLeave={onHoverEnd}>
            {showBot && (
                <div className="sharebot-container">
                    <div className="sharebot-header">
                        <h2>{t('v3:sharebot')}</h2>
                        <button className="sharebot-close-button" onClick={toggleBot}>×</button>
                    </div>

                    <ChatHeader
                        t={t}
                        activeBotResource={activeBotResource}
                        setActiveBotResource={setActiveBotResource}
                    />

                    <div className="sharebot-messages" ref={chatContainerRef}>
                        <ChatView
                            key={activeBotResource}
                            user={user}
                            conversationHistory={conversationHistory}
                            setConversationHistory={setConversationHistory}
                            activeBotResource={activeBotResource}
                            botAvatar={botAvatar}
                        />
                    </div>

                    <div className="sharebot-input">
                        <textarea
                            ref={textareaRef}
                            value={message}
                            onChange={handleMessageChange}
                            onKeyDown={handleSendMessage}
                            placeholder={t('v3:sharebot_help_message')}
                            rows={1}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}

const ChatHeader = ({ t, activeBotResource, setActiveBotResource }) => {
    return (
        <div className="sharebot-tabs">
            <div
                className={`sharebot-tab ${activeBotResource === shareBotResources.organization ? 'active' : ''}`}
                role="button"
                onClick={() => setActiveBotResource(shareBotResources.organization)}
            >
                {t('v3:my_organization')}
            </div>
            <div
                className={`sharebot-tab ${activeBotResource === shareBotResources.worldKnowledge ? 'active' : ''}`}
                role="button"
                onClick={() => setActiveBotResource(shareBotResources.worldKnowledge)}
            >
                {t('v3:world_knowledge')}
            </div>
        </div>
    );
};

export default ShareBot;